import React, { useEffect, useState } from "react";

/*
	Campos de fecha y hora que se ven y se escriben igual en cualquier navegador (QA 2026-10-09): el <input type="date"> del
	navegador en inglés pone mm/dd/aaaa y 24 h. Aquí siempre dd/mm/aaaa y am/pm. El valor hacia afuera es el de siempre (lo que
	el servidor ya entiende): fecha "2026-10-09", hora "14:30", fecha y hora "2026-10-09T14:30".
*/

const isoADmy = (v: string) => (/^\d{4}-\d{2}-\d{2}/.test(v || "") ? v.slice(8, 10) + "/" + v.slice(5, 7) + "/" + v.slice(0, 4) : "");
export const dmyAIso = (t: string): string | null => {
	const m = (t || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (!m) return null;
	const d = +m[1], mo = +m[2], y = +m[3];
	const f = new Date(y, mo - 1, d);
	if (f.getFullYear() !== y || f.getMonth() !== mo - 1 || f.getDate() !== d) return null;
	return y + "-" + String(mo).padStart(2, "0") + "-" + String(d).padStart(2, "0");
};
/** "14:30" → { hm: "2:30", ampm: "pm" }. */
const partirHora = (v: string) => {
	const m = (v || "").match(/^(\d{1,2}):(\d{2})/);
	if (!m) return { hm: "", ampm: "am" };
	const h = +m[1];
	return { hm: (h % 12 === 0 ? 12 : h % 12) + ":" + m[2], ampm: h < 12 ? "am" : "pm" };
};
/** "2:30" + "pm" → "14:30" (null si no se entiende). */
export const horaA24 = (hm: string, ampm: string): string | null => {
	const m = (hm || "").trim().match(/^(\d{1,2})(?::(\d{2}))?$/);
	if (!m) return null;
	let h = +m[1];
	const mi = m[2] === undefined ? 0 : +m[2];
	if (h < 1 || h > 12 || mi > 59) return null;
	if (ampm === "pm" && h !== 12) h += 12;
	if (ampm === "am" && h === 12) h = 0;
	return String(h).padStart(2, "0") + ":" + String(mi).padStart(2, "0");
};

/*
	Mientras se escribe (el teclado numérico del iPhone no tiene «/» ni «:»): con solo dígitos se pone el separador solo
	("15102026" → "15/10/2026", "930" → "9:30", "1130" → "11:30"); si la persona escribe «/», «-», «.» o «:», se respetan. El
	separador se agrega solo cuando ya hay dígitos después (así borrar hacia atrás no lo vuelve a poner).
*/
export const formatearFecha = (raw: string): string => {
	const t = (raw || "").replace(/[-.]/g, "/").replace(/[^\d/]/g, "");
	if (t.includes("/")) {
		// Con separadores: lo que sobra de una parte pasa a la siguiente («15/102» → «15/10/2»).
		const tk = t.split("/");
		let d = tk[0];
		let m: string | null = tk.length > 1 ? tk[1] : null;
		let y: string | null = tk.length > 2 ? tk.slice(2).join("") : null;
		if (d.length > 2) { m = d.slice(2) + (m ?? ""); d = d.slice(0, 2); }
		if (m !== null && m.length > 2) { y = m.slice(2) + (y ?? ""); m = m.slice(0, 2); }
		return d + (m !== null ? "/" + m : "") + (y !== null ? "/" + y.slice(0, 4) : "");
	}
	const d = t.slice(0, 8);
	if (d.length <= 2) return d;
	if (d.length <= 4) return d.slice(0, 2) + "/" + d.slice(2);
	return d.slice(0, 2) + "/" + d.slice(2, 4) + "/" + d.slice(4);
};
export const formatearHora = (raw: string): string => {
	const t = (raw || "").replace(/[.]/g, ":").replace(/[^\d:]/g, "");
	if (t.includes(":")) {
		const [h = "", m = ""] = t.split(":");
		return h.slice(0, 2) + ":" + m.replace(/:/g, "").slice(0, 2);
	}
	const d = t.slice(0, 4);
	if (d.length <= 2) return d;
	return d.length === 3 ? d.slice(0, 1) + ":" + d.slice(1) : d.slice(0, 2) + ":" + d.slice(2);
};

const mal: React.CSSProperties = { borderColor: "var(--wa-oro)" };
const nota: React.CSSProperties = { fontSize: 12, color: "var(--wa-oro)" };

/** Fecha dd/mm/aaaa ↔ "AAAA-MM-DD". */
export const FechaDMY: React.FC<{ value: string; onChange: (iso: string) => void; disabled?: boolean; className?: string }> = ({ value, onChange, disabled, className }) => {
	const [t, setT] = useState<string>(isoADmy(value));
	const [error, setError] = useState<boolean>(false);
	useEffect(() => { setT(isoADmy(value)); setError(false); }, [value]);
	const leer = () => {
		if (t.trim() === "") { setError(false); onChange(""); return; }
		const iso = dmyAIso(t);
		setError(iso === null);
		if (iso !== null) onChange(iso);
	};
	return (
		<span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
			<input type="text" inputMode="numeric" className={className || "form-control"} placeholder="dd/mm/aaaa" value={t} disabled={disabled}
				style={error ? mal : undefined} onChange={(e) => setT(formatearFecha(e.target.value))} onBlur={leer} />
			{error ? <span style={nota}>Va como dd/mm/aaaa (por ejemplo 09/10/2026).</span> : null}
		</span>
	);
};

/** Hora h:mm + am/pm ↔ "HH:MM" (vacío = sin hora). */
export const HoraAmPm: React.FC<{ value: string; onChange: (hhmm: string) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
	const p = partirHora(value);
	const [hm, setHm] = useState<string>(p.hm);
	const [ampm, setAmpm] = useState<string>(p.ampm);
	const [error, setError] = useState<boolean>(false);
	useEffect(() => { const q = partirHora(value); setHm(q.hm); setAmpm(q.ampm); setError(false); }, [value]);
	const emitir = (h: string, a: string) => {
		if (h.trim() === "") { setError(false); onChange(""); return; }
		const v = horaA24(h, a);
		setError(v === null);
		if (v !== null) onChange(v);
	};
	return (
		<span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
			<span style={{ display: "flex", gap: 6 }}>
				<input type="text" inputMode="numeric" className="form-control" placeholder="h:mm" value={hm} disabled={disabled} style={{ minWidth: 0, ...(error ? mal : {}) }}
					onChange={(e) => setHm(formatearHora(e.target.value))} onBlur={() => emitir(hm, ampm)} />
				<select className="form-select" value={ampm} disabled={disabled} style={{ width: "auto" }} onChange={(e) => { setAmpm(e.target.value); emitir(hm, e.target.value); }}>
					<option value="am">am</option><option value="pm">pm</option>
				</select>
			</span>
			{error ? <span style={nota}>Va como 8:00 o 8:30, con am o pm.</span> : null}
		</span>
	);
};

/** Fecha dd/mm/aaaa y hora opcional en am/pm ↔ "AAAA-MM-DDTHH:MM" (o solo "AAAA-MM-DD" si no lleva hora: el día completo). */
export const FechaHoraDMY: React.FC<{ value: string; onChange: (v: string) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
	const fecha = (value || "").slice(0, 10);
	// La hora se puede escribir antes que la fecha: se guarda aquí y sale junto con la fecha en cuanto la haya.
	const [hora, setHora] = useState<string>((value || "").length > 10 ? (value || "").slice(11, 16) : "");
	useEffect(() => { if ((value || "").length > 10) setHora((value || "").slice(11, 16)); }, [value]);
	return (
		<span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
			<span style={{ flex: "1 1 130px", minWidth: 0 }}><FechaDMY value={fecha} disabled={disabled} onChange={(f) => onChange(f ? f + (hora ? "T" + hora : "") : "")} /></span>
			<span style={{ flex: "1 1 150px", minWidth: 0 }}><HoraAmPm value={hora} disabled={disabled} onChange={(h) => { setHora(h); if (fecha) onChange(fecha + (h ? "T" + h : "")); }} /></span>
		</span>
	);
};
